import type {
  AcceptOrgInvitationRequest,
  AcceptOrgInvitationResponse,
  CreateOrgInvitationRequest,
  CreateOrgInvitationResponse,
  GetOrgInvitationResponse,
  ListOrgInvitationsResponse,
  ListOrgMembersResponse,
  RevokeOrgInvitationResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/orgs/*` + `/v1/invitations/*`
 * (PR-E.1 — N:1 org membership). See `apps/platform-api/src/routes/v1-orgs.ts`
 * for the endpoint definitions.
 */

export async function listOrgMembers(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgMembersResponse> {
  return platformApiRequest<ListOrgMembersResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members`,
    { signal },
    fetchImpl,
  );
}

export async function listOrgInvitations(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgInvitationsResponse> {
  return platformApiRequest<ListOrgInvitationsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations`,
    { signal },
    fetchImpl,
  );
}

export async function createOrgInvitation(
  orgId: string,
  body: CreateOrgInvitationRequest,
  fetchImpl: typeof fetch,
): Promise<CreateOrgInvitationResponse> {
  return platformApiRequest<CreateOrgInvitationResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function revokeOrgInvitation(
  orgId: string,
  invitationId: string,
  fetchImpl: typeof fetch,
): Promise<RevokeOrgInvitationResponse> {
  return platformApiRequest<RevokeOrgInvitationResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function getInvitation(
  token: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetOrgInvitationResponse> {
  return platformApiRequest<GetOrgInvitationResponse>(
    `/v1/invitations/${encodeURIComponent(token)}`,
    { signal },
    fetchImpl,
  );
}

export async function acceptInvitation(
  token: string,
  body: AcceptOrgInvitationRequest,
  fetchImpl: typeof fetch,
): Promise<AcceptOrgInvitationResponse> {
  return platformApiRequest<AcceptOrgInvitationResponse>(
    `/v1/invitations/${encodeURIComponent(token)}/accept`,
    { method: 'POST', body },
    fetchImpl,
  );
}
